import { z } from 'zod'
import { DEVISE_PAR_DEFAUT, MOYENS_PAIEMENT, SENS_PAIEMENT } from './domaine'

// Schémas d'entrée : paiements, import de relevé, rapprochement manuel, délais de grâce.

const id = z.uuid()
const jourCivil = z.iso.date()
const texte = z.string().trim().min(1)
const devise = z.string().regex(/^[A-Z]{3}$/)

const champsPaiement = {
  tiersId: id.nullable(),
  echeanceId: id.nullable(),
  compteId: id.nullable(),
  libelle: texte.nullable(),
  montantDuCts: z.number().int().positive(),
  devise,
  datePrevue: jourCivil.nullable(),
  moyen: z.enum(MOYENS_PAIEMENT).nullable(),
}

export const paiementCreation = z.object({
  sens: z.enum(SENS_PAIEMENT),
  entiteId: id,
  ...champsPaiement,
  tiersId: champsPaiement.tiersId.optional(),
  echeanceId: champsPaiement.echeanceId.optional(),
  compteId: champsPaiement.compteId.optional(),
  libelle: champsPaiement.libelle.optional(),
  devise: devise.default(DEVISE_PAR_DEFAUT),
  datePrevue: champsPaiement.datePrevue.optional(),
  moyen: champsPaiement.moyen.optional(),
  /** Pièce qui appelle ce paiement (facture, avis) : même entité, liée une fois pour toutes. */
  pieceId: id.optional(),
})

export const paiementModification = z.object(champsPaiement).partial()

/** Marquer payé en un clic, ou plusieurs à la fois. */
export const marquerPayes = z.object({ paiementIds: z.array(id).min(1).max(200) })

/** Relevé CSV en base64 (l'encodage du fichier est détecté à l'analyse). */
export const importReleve = z.object({
  compteId: id,
  nomFichier: texte.max(255),
  contenuBase64: z.string().min(4).max(14_000_000),
  /** Date de l'export ; « relevé couvert jusqu'au » vaut sa veille. Défaut : lue dans le fichier, sinon aujourd'hui. */
  dateExport: jourCivil.optional(),
})

export const rapprochementManuel = z.object({
  transactionId: id,
  affectations: z
    .array(
      z.object({
        paiementId: id,
        montantAffecteCts: z
          .number()
          .int()
          .refine((n) => n !== 0, 'Montant non nul'),
      }),
    )
    .min(1)
    .max(20),
})

/** Compenser une opération du relevé par une autre, qui l'annule (signe contraire, même montant). */
export const compensationManuelle = z.object({ avecId: id })

export const delaiGrace = z.object({ jours: z.number().int().min(0).max(120), ouvres: z.boolean() })
export const delaisGrace = z.object(
  Object.fromEntries(MOYENS_PAIEMENT.map((m) => [m, delaiGrace])) as {
    [K in (typeof MOYENS_PAIEMENT)[number]]: typeof delaiGrace
  },
)

export type PaiementCreation = z.infer<typeof paiementCreation>
export type PaiementModification = z.infer<typeof paiementModification>
export type ImportReleve = z.infer<typeof importReleve>
export type RapprochementManuel = z.infer<typeof rapprochementManuel>
